import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './RightSidebar.css';
import API_URL from '../../config';

const recentActivities = [
  { id: 1, type: 'create', text: 'Hesab\u0131n\u0131z olu\u015fturuldu', time: 'Az \u00f6nce', icon: '\u2713' },
  { id: 2, type: 'info', text: '\u00dccretsiz plan aktif edildi', time: 'Az \u00f6nce', icon: '\u2605' },
  { id: 3, type: 'tip', text: '\u0130lk projenizi olu\u015fturun!', time: '\u0130pucu', icon: '\u2192' },
];

// Tüm bildirim tipleri için Türkçe etiket — eksik tip kalmasın diye, yeni
// notification türü eklendiğinde buraya da satır eklenmeli.
const notifTypeLabels = {
  like: 'gönderinizi beğendi',
  comment: 'gönderinize yorum yaptı',
  follow: 'sizi takip etmeye başladı',
  solution: 'cevabınızı çözüm olarak işaretledi',
  reply: 'konunuza cevap yazdı',
  dm: 'size mesaj gönderdi',
  project_stakeholder: 'sizi paydaş olarak ekledi',
  project_stakeholder_invitation: 'sizi paydaş olmaya davet etti',
  project_team: 'sizi ekibe ekledi',
  project_review_invitation: 'projesini incelemenizi istiyor',
  project_review_response: 'davetinize yanıt verdi',
  org_invite: 'sizi organizasyona davet etti',
  group_join: 'grubunuza katıldı',
  group_invite: 'sizi gruba davet etti',
  team_invite: 'sizi ekibe davet etti',
  team_invite_response: 'davetinize yanıt verdi',
  partnership_request: 'ortaklık teklif ediyor',
  template_update: 'şablonu güncelledi',
  doc_update: 'dokümanı güncelledi',
  badge_earned: 'yeni rozet kazandınız',
  job_match: 'yeni iş ilanı eşleşmesi',
};

function getRelativeTime(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Az önce';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} dk önce`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} saat önce`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} gün önce`;
  return date.toLocaleDateString('tr-TR');
}

function getNotifText(notif) {
  // Tüm "message" alanı dolu bildirimler (paydaş daveti, davet yanıtı,
  // grup, takım, organizasyon, partner, doküman, rozet, vs.) doğrudan
  // backend'in yazdığı Türkçe metni gösterir.
  if (notif.message) return notif.message;
  // Aksi halde aktör adı + Türkçe etiketten oluştur. Bilinmeyen tip için
  // raw type yerine genel Türkçe ifade ver — İngilizce sızmasın.
  const actorName = notif.actor?.firstName
    ? `${notif.actor.firstName} ${notif.actor.lastName || ''}`.trim()
    : 'Birisi';
  const label = notifTypeLabels[notif.type] || 'sizinle ilgili bir aktivite gerçekleştirdi';
  return `${actorName} ${label}`;
}

const RightSidebar = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('projabze_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(Array.isArray(data) ? data : data.notifications || []);
      }
    } catch (err) {
      console.error('Bildirim yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const markAllRead = async () => {
    const token = localStorage.getItem('projabze_token');
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Okundu işaretlenemedi:', err);
    }
  };

  const handleNotifClick = async (notif) => {
    // Mark as read
    if (!notif.isRead) {
      const token = localStorage.getItem('projabze_token');
      if (token) {
        try {
          await fetch(`${API_URL}/api/notifications/${notif.id}/read`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` },
          });
          setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n));
        } catch { /* ignore */ }
      }
    }
    // Navigate based on entity type — use state with timestamp to force re-render even on same page
    if (notif.entityType === 'project_invitation' && notif.entityId) {
      navigate(`/projects?invitation=${notif.entityId}`, { state: { ts: Date.now() } });
    } else if (notif.entityType === 'project' && notif.entityId) {
      navigate(`/projects?id=${notif.entityId}`, { state: { ts: Date.now() } });
    } else if (notif.entityType === 'organization' && notif.entityId) {
      navigate(`/groups?org=${notif.entityId}`, { state: { ts: Date.now() } });
    } else if (notif.entityType === 'team' && notif.entityId) {
      navigate(`/groups?team=${notif.entityId}`, { state: { ts: Date.now() } });
    } else if (notif.entityType === 'message' && notif.entityId) {
      navigate(`/messages?conv=${notif.entityId}`, { state: { ts: Date.now() } });
    }
  };

  return (
    <aside className="app-right-sidebar">
      {/* Son Hareketler */}
      <div className="app-right-section">
        <h3>Son Hareketler</h3>
        <div className="app-right-activity-feed">
          {recentActivities.map(act => (
            <div className={`app-right-activity-item ${act.type}`} key={act.id}>
              <div className="app-right-activity-dot">{act.icon}</div>
              <div className="app-right-activity-content">
                <span className="app-right-activity-text">{act.text}</span>
                <span className="app-right-activity-time">{act.time}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bildirimler */}
      <div className="app-right-section">
        <div className="app-right-notif-header">
          <h3>
            Bildirimler
            {unreadCount > 0 && <span className="app-right-notif-badge">{unreadCount}</span>}
          </h3>
          {unreadCount > 0 && (
            <button className="app-right-notif-read-all" onClick={markAllRead}>
              Tümünü Okundu Yap
            </button>
          )}
        </div>
        {loading ? (
          <div className="app-right-notif-empty">Yükleniyor...</div>
        ) : notifications.length === 0 ? (
          <div className="app-right-notif-empty">Yeni bildirim yok</div>
        ) : (
          <div className="app-right-notif-list">
            {notifications.map(notif => (
              <div
                className={`app-right-notif-item ${notif.isRead ? '' : 'unread'} ${notif.entityId ? 'clickable' : ''}`}
                key={notif.id || notif._id}
                onClick={() => notif.entityId && handleNotifClick(notif)}
              >
                {!notif.isRead && <span className="app-right-notif-dot" />}
                <div className="app-right-notif-content">
                  <span className="app-right-notif-text">{getNotifText(notif)}</span>
                  <span className="app-right-notif-time">{getRelativeTime(notif.createdAt || notif.date)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

export default RightSidebar;
